<template>
  <coozzy-card :title="$t('message.settings.signature.title')">
    <div
      v-if="loading"
      class="row">
      <div class="col">
        <coozzy-spinner />
      </div>
    </div>
    <template v-else>
      <div
        v-for="(signature, index) in signatures"
        :key="signature.id || signature._localKey"
        class="row">
        <div class="col-sm-12 col-md-8 mb-2">
          <coozzy-form-input
            v-model="signature.name"
            :name="$tc('message.generic.name')" />
        </div>
        <div class="col-sm-12 col-md-8">
          <editor
            ref="editor-tiny"
            v-model="signature.signature"
            :api-key="editorApi"
            :init="editorInit" />
        </div>
        <div class="col-sm-12 col-md-4">
          <h5>{{ $t('message.settings.availablePlaceholders') }}</h5>
          <div
            v-for="placeholder in availablePlaceholders"
            :key="placeholder">
            {{ placeholder }}
          </div>
        </div>
        <div
          v-if="index > 0"
          class="col-sm-12 col-md-8">
          <coozzy-button
            class="float-right mr-3 mt-3"
            design="red"
            @click="deleteNewSignature(index)">
            {{ $t('message.settings.signature.deleteNewSignature') }}
          </coozzy-button>
        </div>
      </div>
      <div class="row">
        <div class="col">
          <coozzy-button
            class="float-right mt-3"
            design="primary"
            @click="saveClicked">
            {{ $t('message.generic.form.save') }}
          </coozzy-button>
          <coozzy-button
            class="float-right mr-3 mt-3"
            design="prop-green"
            @click="addNewSignature">
            {{ $t('message.settings.signature.addNewSignature') }}
          </coozzy-button>
        </div>
      </div>
    </template>
  </coozzy-card>
</template>

<script>
import CoozzyCard from '../../framework/components/card/CoozzyCard'
import CoozzyButton from '../../framework/components/button/CoozzyButton'
import CoozzySpinner from '../../framework/components/misc/CoozzySpinner'
import MessageApi from '../../misc/apis/MessageApi'
import { user } from '@/mixins/user'
import Vue from 'vue'
import { mapActions } from 'vuex'
import Editor from '@tinymce/tinymce-vue'
import MediaApi from '@/misc/apis/MediaApi'
import { media } from '@/mixins/media'
import CoozzyFormInput from '@/framework/components/form/input/CoozzyFormInput.vue'

export default {
  name: 'SignatureSettings',
  components: {
    CoozzyFormInput,
    CoozzySpinner,
    CoozzyButton,
    CoozzyCard,
    Editor
  },
  mixins: [user, media],
  data: () => {
    return {
      loading: false,
      signatures: [{
        signature: '',
        name: 'Standard',
        _localKey: `new-${Date.now()}`
      }],
      savedSignaturesSnapshot: {},
      availablePlaceholders: [
        '#user.firstName#',
        '#user.lastName#',
        '#user.phoneNumber#',
        '#user.email#',
        '#user.jobTitle#'
      ]
    }
  },
  computed: {
    editorApi() {
      return 'gpl'
    },
    fontSetting() {
      return this.$store.getters['user/getFontSettings']
    },
    contentStyle() {
      const fontFamily = this.getConfiguredFontFamily()
      const fontSize = this.getConfiguredFontSize()
      const fontColor = this.getConfiguredFontColor()
      return `body { color: ${fontColor} !important; font-family: ${fontFamily} !important; font-size: ${fontSize};} p {margin: 0}`
    },
    fontsizeFormats() {
      let sizeOptions = []
      for (let i = 4; i <= 36; i++) {
        sizeOptions.push(`${i}pt`)
      }
      return sizeOptions.join(' ')
    },
    fontFormats() {
      const defaultFont = 'Lato=Lato, sans-serif;'
      const userFont = this.fontSetting && this.fontSetting.family !== '' ? `${this.fontSetting.family}=${this.fontSetting.family}, sans-serif;` : defaultFont
      return `${userFont}`
    },
    editorInit() {
      return {
        toolbar: 'undo redo | bold italic underline forecolor | fontsize | numlist bullist link table | image | code | fullscreen print',
        plugins: 'fullscreen lists code image table link',
        height: '370',
        menubar: '',
        language: this.currentLanguage,
        elementpath: false,
        automatic_uploads: true,
        images_upload_handler: this.uploadImage,
        license_key: 'gpl',
        content_style: this.contentStyle,
        font_formats: this.fontFormats,
        branding: false,
        remove_trailing_brs: false,
        browser_spellcheck: true,
        table_toolbar: '',
        font_size_formats: this.fontsizeFormats,
        forced_root_block: 'div',
        verify_html: false,
        setup: (editor) => this.setupSignatureEditor(editor)
      }
    }
  },
  watch: {},
  mounted() {
    this.getSignature()
  },
  methods: {
    ...mapActions('message', ['resetSignatures', 'loadSignatureForUserId']),
    getConfiguredFontFamily() {
      return this.fontSetting?.family || 'Lato, sans-serif'
    },
    getConfiguredFontSize() {
      return (this.fontSetting?.size || 10) + 'pt'
    },
    getConfiguredFontColor() {
      const color = this.fontSetting?.color
      if (typeof color === 'string' && color.trim() !== '') {
        return color
      }
      if (color && typeof color === 'object' && typeof color.red === 'number') {
        return `rgba(${color.red}, ${color.green}, ${color.blue}, ${color.alpha})`
      }
      return '#495057'
    },
    removeFontFamilyOnly(rootElement) {
      if (!rootElement?.querySelectorAll) return

      const allElements = [rootElement, ...rootElement.querySelectorAll('*')]
      allElements.forEach(element => {
        element.style?.removeProperty('font-family')

        if (element.hasAttribute('face')) element.removeAttribute('face')

        if (element.getAttribute('style') === '') {
          element.removeAttribute('style')
        }
      })

      rootElement.querySelectorAll('font').forEach(fontElement => {
        const parent = fontElement.parentNode
        if (!parent) return
        while (fontElement.firstChild) {
          parent.insertBefore(fontElement.firstChild, fontElement)
        }
        parent.removeChild(fontElement)
      })
    },
    normalizeSignatureFontStyles(signatureHtml) {
      if (!signatureHtml) return ''
      const tempDiv = document.createElement('div')
      tempDiv.innerHTML = signatureHtml

      // Keep TinyMCE forecolor / inline colors; only strip font-family overrides
      // so account Email Font Settings still control the default family.
      this.removeFontFamilyOnly(tempDiv)

      return tempDiv.innerHTML
    },
    buildSignatureSnapshot(signatures) {
      const snapshot = {}
      signatures.forEach(signature => {
        if (!signature.id) return
        snapshot[signature.id] = {
          name: signature.name || '',
          signature: this.normalizeSignatureForCompare(signature.signature || '')
        }
      })
      return snapshot
    },
    normalizeSignatureForCompare(signatureHtml) {
      return this.normalizeSignatureFontStyles(signatureHtml)
        ?.replaceAll(' crossorigin="use-credentials"', '') || ''
    },
    isSignatureDirty(signature, signatureBody) {
      if (!signature.id) return true
      const saved = this.savedSignaturesSnapshot[signature.id]
      if (!saved) return true
      return saved.name !== (signature.name || '') || saved.signature !== signatureBody
    },
    setupSignatureEditor(editor) {
      editor.on('paste_preprocess', (plugin, args) => {
        const tempDiv = document.createElement('div')
        tempDiv.innerHTML = args.content
        this.removeFontFamilyOnly(tempDiv)
        args.content = tempDiv.innerHTML
      })

      editor.on('paste_postprocess', (plugin, args) => {
        this.removeFontFamilyOnly(args.node)
      })

      editor.on('NodeChange', function() {
        const brs = editor.getBody().getElementsByTagName('br')

        Array.from(brs).forEach(br => {
          const parent = br.parentNode

          const prevSibling = br.previousSibling
          if (prevSibling && prevSibling.nodeName === 'SPAN' &&
            prevSibling.getAttribute('data-mce-type') === 'format-caret') {
            const formatSpan = prevSibling.getElementsByTagName('span')[0]
            if (formatSpan && formatSpan.style.fontSize) {
              const fontSize = formatSpan.style.fontSize
              parent.style.fontSize = fontSize
              formatSpan.setAttribute('data-mce-preserved', 'true')
            }
          }

          if (parent && (!parent.style.fontSize || parent.style.fontSize === '')) {
            const defaultFontSize = editor.getBody().style.fontSize || '10pt'
            parent.style.fontSize = defaultFontSize
          }
        })
      })
    },
    updateEditorContentStyle() {
      const editors = this.$refs['editor-tiny']
      const editorList = Array.isArray(editors) ? editors : (editors ? [editors] : [])
      editorList.forEach(editorRef => {
        const editor = editorRef.editor
        if (editor && editor.getBody()) {
          editor.getBody().style.fontFamily = this.getConfiguredFontFamily()
          editor.getBody().style.fontSize = this.getConfiguredFontSize()
          editor.getBody().style.color = this.getConfiguredFontColor()
        }
      })
    },
    getSignature() {
      MessageApi.getSignatures(this.accountId)
        .then(response => {
          this.signatures = (response.signatures || []).map(signature => ({
            ...signature,
            _localKey: signature.id || `new-${Date.now()}-${Math.random().toString(36).slice(2)}`
          }))
          if (this.signatures.length === 0) {
            this.signatures.push({
              signature: '',
              name: 'Standard',
              _localKey: `new-${Date.now()}`
            })
          }
          this.savedSignaturesSnapshot = this.buildSignatureSnapshot(this.signatures)
        })
        .catch(e => {
          if (!e.response || e.response.status !== 404) {
            console.log(e)
            Vue.toasted.show(this.$t('message.loadingErrors.signature'), { type: 'error' })
          }
        })
        .finally(() => {
          this.loading = false
          this.$nextTick(() => {
            this.updateEditorContentStyle()
            this.scheduleSavedSnapshotRefresh()
          })
        })
    },
    scheduleSavedSnapshotRefresh(attempt = 0) {
      const editors = this.$refs['editor-tiny']
      const editorList = Array.isArray(editors) ? editors : (editors ? [editors] : [])
      const allReady = editorList.length > 0 && editorList.every(ref => ref?.editor?.getBody())
      if (allReady || attempt >= 10) {
        this.refreshSavedSnapshotFromEditors()
        return
      }
      setTimeout(() => this.scheduleSavedSnapshotRefresh(attempt + 1), 100)
    },
    async refreshSavedSnapshotFromEditors() {
      const editors = this.$refs['editor-tiny']
      const editorList = Array.isArray(editors) ? editors : (editors ? [editors] : [])
      const snapshot = {}
      for (let index = 0; index < this.signatures.length; index++) {
        const signature = this.signatures[index]
        if (!signature.id) continue
        const editorContent = editorList[index]?.editor?.getContent()
        snapshot[signature.id] = {
          name: signature.name || '',
          signature: this.normalizeSignatureForCompare(editorContent || signature.signature || '')
        }
      }
      this.savedSignaturesSnapshot = Object.keys(snapshot).length > 0
        ? snapshot
        : this.buildSignatureSnapshot(this.signatures)
    },
    addNewSignature() {
      this.signatures.push({
        signature: '',
        name: '',
        _localKey: `new-${Date.now()}-${Math.random().toString(36).slice(2)}`
      })
    },
    deleteNewSignature(index) {
      if (this.signatures[index].id) {
        MessageApi.deleteSignature(this.signatures[index].id)
          .then(() => {
            Vue.toasted.show(this.$t('message.settings.signature.deletedSuccessfully'), { type: 'success' })
            const deletedId = this.signatures[index].id
            this.signatures = this.signatures.filter((sign) => sign.id !== deletedId)
            if (deletedId && this.savedSignaturesSnapshot[deletedId]) {
              this.$delete(this.savedSignaturesSnapshot, deletedId)
            }
          })
          .catch(e => {
            console.log(e)
            Vue.toasted.show(this.$t('message.savingErrors.signature'), { type: 'error' })
          })
      } else {
        this.signatures.splice(index, 1)
      }
    },
    async saveClicked() {
      const editors = this.$refs['editor-tiny']
      const editorList = Array.isArray(editors) ? editors : (editors ? [editors] : [])
      const promises = []
      for (let index = 0; index < this.signatures.length; index++) {
        const editorContent = await editorList[index]?.editor?.getContent()
        const currentSignature = editorContent || this.signatures[index].signature || ''
        let signatureBody = this.normalizeSignatureFontStyles(currentSignature)
        signatureBody = signatureBody?.replaceAll(' crossorigin="use-credentials"', '')

        if (!this.isSignatureDirty(this.signatures[index], signatureBody)) {
          continue
        }

        promises.push(MessageApi.setSignatureWithId(this.accountId, signatureBody, this.signatures[index].name, this.signatures[index].id))
      }

      if (promises.length === 0) {
        Vue.toasted.show(this.$t('message.settings.signature.savedSuccessfully'), { type: 'success' })
        return
      }

      this.loading = true
      Promise.all(promises).then((response) => {
        const signatureId = response.length > 0 ? response[0].signature.id : ''
        this.resetSignatures()
        this.getSignature()
        this.loadSignatureForUserId({
          accountId: this.accountId,
          userId: this.userId,
          signatureId: signatureId
        })
        Vue.toasted.show(this.$t('message.settings.signature.savedSuccessfully'), { type: 'success' })
        this.$nextTick(() => {
          this.loading = false
        })
      })
        .catch(e => {
          console.log(e)
          Vue.toasted.show(this.$t('message.savingErrors.signature'), { type: 'error' })
          this.loading = false
        })
    },
    async uploadImage(blobInfo, success, failure) {
      // const imageSize = blobInfo.blob().size / 1000
      // const maxSize = 10000000
      // if (imageSize > maxSize) {
      //   failure('Image is too large( ' + imageSize + ') ,Maximum image size is:' + maxSize + ' kB')
      //   return
      // } else {
      //   console.log('not too big')
      // }
      if (blobInfo) {
        const formData = new FormData()
        const fileName = this.editFileName(blobInfo.filename())
        formData.append('file', blobInfo.blob(), fileName)
        formData.append('filename', blobInfo.filename())
        formData.append('public', 'true')
        try {
          const response = await MediaApi.uploadMedia(formData)
          return response.url
        } catch (e) {
          console.log(e)
          if (e.response && e.response.data && e.response.data.message === 'Not supported MIME type') {
            Vue.toasted.show(this.$t('message.savingErrors.fileNotSupported'), { type: 'error' })
          } else {
            Vue.toasted.show(this.$t('message.savingErrors.file'), { type: 'error' })
          }
          return e
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
</style>
